import { useLayoutEffect, useRef, useState } from 'react';

import { Tooltip, type TypeTooltipProps } from '@thefarmersfront/kitchen';

import type { CustomCellRendererProps } from 'ag-grid-react';
import clsx from 'clsx';

import { collapseLineBreaks } from '~/utils/common';

import styles from './TableCellRenderer.module.scss';

export type TypeTooltipTextCellParams = {
  placement?: TypeTooltipProps['placement'];
  tooltipMaxWidth?: TypeTooltipProps['maxWidth'];
  /**
   * 연속·앞뒤 공백을 셀에 그대로 노출한다(기본 false: 브라우저가 공백을 병합).
   * 줄바꿈은 공백으로 치환해 셀을 단일 라인으로 유지하고(고정 높이 행 레이아웃 보호),
   * 툴팁에는 원본을 그대로(pre-wrap) 보여준다.
   */
  preserveWhitespace?: boolean;
};

export type TypeTooltipTextCellProps<TData, TValue> = TypeTooltipTextCellParams &
  CustomCellRendererProps<TData, TValue>;

const TooltipTextCell = <TData, TValue>({
  value,
  valueFormatted,
  placement,
  tooltipMaxWidth,
  preserveWhitespace = false,
}: TypeTooltipTextCellProps<TData, TValue>) => {
  const text = valueFormatted ?? (value == null ? '' : String(value));
  // 셀은 고정 높이라 줄바꿈만 공백으로 치환해 단일 라인을 유지한다(툴팁 description에는 원본 text를 넘긴다).
  const displayText = preserveWhitespace ? collapseLineBreaks(text) : text;

  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const checkTruncated = () => setIsTruncated(element.scrollWidth > element.clientWidth);

    checkTruncated();

    const observer = new ResizeObserver(checkTruncated);
    observer.observe(element);
    return () => observer.disconnect();
  }, [displayText]);

  if (!text || text === '-') return '-';

  return (
    <Tooltip
      className={styles['tooltip-text-cell']}
      description={text}
      placement={placement}
      maxWidth={tooltipMaxWidth}
      disabled={!isTruncated}
      portalTarget={document.body}
    >
      <span
        ref={textRef}
        className={clsx(styles['tooltip-text-cell-text'], preserveWhitespace && styles['preserve-whitespace'])}
      >
        {displayText}
      </span>
    </Tooltip>
  );
};

export default TooltipTextCell;
